/*
 * enquiry-context — server-first "enquiry store" hydration signal.
 *
 * The server renders a hidden #enquiry-context block per page exposing the current
 * page's context as data attributes (resources/views/partials/_enquiry-context
 * .antlers.html). This module copies those values — plus UTM params captured ONCE
 * from the landing URL — into `context_*` hidden inputs of every POST <form> on the
 * page. It runs on first load AND on every `psd:page-view`, so it covers both:
 *   - forms embedded in page content INSIDE #swup — Swup replaces them each
 *     navigation, so the fresh markup is (re-)hydrated on page:view; and
 *   - a persistent global modal form OUTSIDE #swup — never destroyed by Alpine's
 *     per-navigation teardown, so its inputs are UPDATED in place each page:view.
 *
 * This is the plain-module counterpart to an Alpine store (NOT an Alpine directive)
 * — CSP-safe (no inline expressions), it just reads server-rendered DOM and mutates
 * hidden inputs. Wired in resources/js/app.js (init on boot + on PAGE_VIEW_EVENT).
 *
 * PROGRESSIVE ENHANCEMENT: with JS off the forms simply submit without context —
 * the fields are optional in the blueprint, so a no-JS enquiry still succeeds.
 *
 * The stored field handles are the `content_context` fieldset's, imported with the
 * `context_` prefix into the form blueprint; the input `name` is `context_<key>`,
 * so a submission carries the context straight into Statamic's stored data.
 */

/** Hidden-input name prefix — matches the `prefix: context_` fieldset import. */
const FIELD_PREFIX = 'context_';

/**
 * Query params captured once from the landing URL and persisted across visits.
 *
 * EMPTY FOR KLC, AND DELIBERATELY SO. This held the five UTM keys plus `gclid` and
 * `fbclid`. Marketing attribution stapled to a health enquiry builds a
 * health-inference profile nobody decided to build — "arrived from a PCOS ad, then
 * enquired" is a health inference, stored indefinitely, on a form that is not a
 * medical record and was never consented to as one.
 *
 * `gclid` and `fbclid` were also being captured with NO field to store them: the
 * content_context fieldset declared none, so they were submitted on every enquiry and
 * silently dropped. Keeping this list in step with that fieldset is the contract.
 *
 * The capture machinery below is intact — a project that needs attribution adds keys
 * here AND the matching fields, together.
 */
const UTM_KEYS = [];

/**
 * Marker attribute stamped on inputs this module owns, so re-runs UPDATE the same
 * input instead of appending duplicates (idempotency across every page:view).
 */
const OWNED_ATTR = 'data-enquiry-context';

/**
 * Once-captured UTM/click-id map. `null` until the first {@link captureUtm} — then
 * frozen for the session, because Swup navigations drop the tracking query string
 * from the URL and we must not lose the original attribution.
 *
 * @type {Record<string, string> | null}
 */
let persistedUtm = null;

/**
 * `pageUrl` (dataset, camelCase) -> `page_url` (field-handle, snake_case).
 *
 * @param {string} key
 * @returns {string}
 */
function camelToSnake(key) {
    return key.replace(/[A-Z]/g, (match) => `_${match.toLowerCase()}`);
}

/**
 * Read the tracking params from the current URL exactly once and retain them for
 * the rest of the session. Empty object when the landing URL carried none.
 *
 * @returns {Record<string, string>}
 */
function captureUtm() {
    if (persistedUtm !== null) {
        return persistedUtm;
    }

    persistedUtm = {};

    try {
        const params = new URLSearchParams(window.location.search);

        for (const key of UTM_KEYS) {
            const value = params.get(key);

            if (value) {
                persistedUtm[key] = value;
            }
        }
    } catch {
        // Malformed URL / no URLSearchParams — leave persistedUtm as {}.
    }

    return persistedUtm;
}

/**
 * The context to hydrate: the server-rendered #enquiry-context values (which win)
 * merged over the persisted UTM params. Empty string values are dropped so a
 * blank data attribute never overwrites a real one on a persistent form.
 *
 * @returns {Record<string, string>}
 */
function readContext() {
    const context = { ...captureUtm() };
    const element = document.getElementById('enquiry-context');

    if (element) {
        for (const key in element.dataset) {
            const value = element.dataset[key];

            if (value !== undefined && value !== '') {
                context[camelToSnake(key)] = value;
            }
        }
    }

    return context;
}

/**
 * Ensure `form` carries one owned hidden input per context key, creating missing
 * inputs and updating existing ones (never duplicating).
 *
 * @param {HTMLFormElement} form
 * @param {Record<string, string>} context
 * @returns {void}
 */
function hydrateForm(form, context) {
    for (const key in context) {
        let input = form.querySelector(`input[${OWNED_ATTR}="${key}"]`);

        if (!input) {
            input = document.createElement('input');
            input.type = 'hidden';
            input.name = `${FIELD_PREFIX}${key}`;
            input.setAttribute(OWNED_ATTR, key);
            form.appendChild(input);
        }

        input.value = context[key];
    }
}

/**
 * Copy the current page context into every eligible form.
 *
 * Only POST forms are hydrated (the enquiry surface); GET forms — e.g. the native
 * `/search` form — are left alone so context never pollutes their query string. A
 * form opts out entirely with `[data-no-enquiry-context]`.
 *
 * Safe to call repeatedly (on boot and on every `psd:page-view`).
 *
 * @returns {void}
 */
export function initEnquiryContext() {
    const context = readContext();

    document.querySelectorAll('form').forEach((form) => {
        if (form.hasAttribute('data-no-enquiry-context')) {
            return;
        }

        const method = (form.getAttribute('method') || 'get').toLowerCase();

        if (method !== 'post') {
            return;
        }

        hydrateForm(form, context);
    });
}
